import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  PaymentRequestConnection,
  UpsertPaymentRequestStatus,
  AccountRequirements,
  DisputeConnection,
  PaymentRequest,
  Dispute,
  SubmitVerificationStatus,
  MutationStatusCode,
  Person,
  PaymentConnection,
  Payment,
} from '../../gql-types.generated';
import { RootState } from '../../store';

interface SliceState {
  accountRequirements?: AccountRequirements;
  involvedUsers?: Person[];
  paymentRequestConnection?: PaymentRequestConnection;
  paymentRequests?: PaymentRequest[];
  disputeConnection?: DisputeConnection;
  disputes?: Dispute[];
  paymentRequestQueryString?: string;
  paymentRequestStatus?: UpsertPaymentRequestStatus;
  reminderStatus?: UpsertPaymentRequestStatus;
  invoiceUniqueId?: string;
  refundStatusCode?: string;
  sendPaymentRequestError?: string;
  verificationStatus?: SubmitVerificationStatus;
  searchError?: Error;
  disputeListError?: Error;
  involvedUsersError?: Error;
  refundError?: Error;
  remindError?: Error;
  verificationsError?: Error;
  accountRequirementsError?: Error;
  isPaymentRequestFormValid: boolean;
  isRecipientEmailValid: boolean;
  isRecipientPhoneValid: boolean;
  isAmountValid: boolean;
  isRefNumValid: boolean;
  isLoadingPaymentRequests: boolean;
  isLoadingDisputes: boolean;
  refundIdempotencyKey?: string;
  paymentConnection?: PaymentConnection;
  payments?: Payment[];
  isLoadingPayments: boolean;
  paymentListError?: Error;
  paymentQueryString?: string;
  refundErrorMessage?: string | null;
  isPaymentRefunded: boolean;
  paymentRefundedAmount?: number;
}

const initialState: SliceState = {
  accountRequirements: undefined,
  involvedUsers: undefined,
  paymentRequestConnection: undefined,
  paymentRequests: undefined,
  disputeConnection: undefined,
  disputes: undefined,
  paymentRequestQueryString: undefined,
  paymentRequestStatus: undefined,
  reminderStatus: undefined,
  invoiceUniqueId: undefined,
  refundStatusCode: undefined,
  sendPaymentRequestError: undefined,
  verificationStatus: undefined,
  isPaymentRequestFormValid: false,
  isRecipientEmailValid: true,
  isRecipientPhoneValid: true,
  isAmountValid: true,
  isRefNumValid: true,
  isLoadingPaymentRequests: true,
  isLoadingDisputes: true,
  refundIdempotencyKey: undefined,
  isLoadingPayments: true,
  refundErrorMessage: undefined,
  isPaymentRefunded: false,
  paymentRefundedAmount: undefined,
};

export const slice = createSlice({
  name: 'home',
  initialState,
  reducers: {
    fetchAccountRequirementsSuccess: (state, action: PayloadAction<AccountRequirements>) => {
      // eslint-disable-next-line no-param-reassign
      state.accountRequirements = action.payload;
    },
    fetchInvolvedUsersSuccess: (state, action: PayloadAction<Person[]>) => {
      const users = [] as Person[];
      const allUsers = action.payload;
      const controller = allUsers.find(person => person.relationship?.involvement?.includes('controller'));
      if (controller) {
        users.push(controller);
      }
      const rep0 = allUsers.find(person => person.relationship?.involvement?.includes('representative-0'));
      if (rep0) {
        users.push(rep0);
      }
      const rep1 = allUsers.find(person => person.relationship?.involvement?.includes('representative-1'));
      if (rep1) {
        users.push(rep1);
      }
      const rep2 = allUsers.find(person => person.relationship?.involvement?.includes('representative-2'));
      if (rep2) {
        users.push(rep2);
      }
      const rep3 = allUsers.find(person => person.relationship?.involvement?.includes('representative-3'));
      if (rep3) {
        users.push(rep3);
      }
      // eslint-disable-next-line no-param-reassign
      state.involvedUsers = users;
    },

    fetchPaymentRequestListSuccess: (state, action: PayloadAction<PaymentRequestConnection>) => {
      // eslint-disable-next-line no-param-reassign
      state.paymentRequestConnection = action.payload;
      // eslint-disable-next-line no-param-reassign
      let items = state.paymentRequests;

      // Initialise the array if this is the first run, or a refresh. We know its a refresh
      // when the connection contains the first page of data.
      if (!items || !state.paymentRequestConnection.pageInfo.hasPreviousPage) {
        items = [];
      }
      action.payload.nodes.map(node => {
        if (!items?.some(i => i.id === node?.id)) {
          items?.push(node as PaymentRequest);
        }
        return node;
      });
      // eslint-disable-next-line no-param-reassign
      state.paymentRequests = items;
    },
    fetchDisputeListSuccess: (state, action: PayloadAction<DisputeConnection>) => {
      // eslint-disable-next-line no-param-reassign
      state.disputeConnection = action.payload;
      // eslint-disable-next-line no-param-reassign
      let items = state.disputes;

      // Initialise the array if this is the first run, or a refresh. We know its a refresh
      // when the connection contains the first page of data.
      if (!items || !state.disputeConnection.pageInfo.hasPreviousPage) {
        items = [];
      }
      action.payload.nodes.map(node => {
        if (!items?.some(i => i.id === node?.id)) {
          items?.push(node as Dispute);
        }
        return node;
      });
      // eslint-disable-next-line no-param-reassign
      state.disputes = items;
    },
    capturePaymentRequestSearch: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line no-param-reassign
      state.paymentRequestQueryString = action.payload;
    },
    capturePaymentRequestStatus: (state, action: PayloadAction<UpsertPaymentRequestStatus>) => {
      // eslint-disable-next-line no-param-reassign
      state.paymentRequestStatus = action.payload;
    },
    captureReminderStatus: (state, action: PayloadAction<UpsertPaymentRequestStatus>) => {
      // eslint-disable-next-line no-param-reassign
      state.reminderStatus = action.payload;
    },
    clearPaymentRequestStatus: state => {
      // eslint-disable-next-line no-param-reassign
      state.paymentRequestStatus = undefined;
    },
    clearReminderStatus: state => {
      // eslint-disable-next-line no-param-reassign
      state.reminderStatus = undefined;
    },
    uploadDocumentSuccess: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line no-param-reassign
      state.invoiceUniqueId = action.payload;
    },
    clearDocument: state => {
      // eslint-disable-next-line no-param-reassign
      state.invoiceUniqueId = undefined;
    },
    uploadDocumentFailure: state => {
      // eslint-disable-next-line no-param-reassign
      state.invoiceUniqueId = 'uploadError';
    },
    captureRefundSuccess: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line no-param-reassign
      state.refundStatusCode = action.payload;
    },
    clearRefundStatus: state => {
      // eslint-disable-next-line no-param-reassign
      state.refundStatusCode = undefined;
    },
    captureSendPaymentRequestError: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line no-param-reassign
      state.sendPaymentRequestError = action.payload;
    },
    clearSendPaymentRequestError: state => {
      // eslint-disable-next-line no-param-reassign
      state.sendPaymentRequestError = undefined;
    },
    captureVerificationStatus: (state, action: PayloadAction<SubmitVerificationStatus>) => {
      // eslint-disable-next-line no-param-reassign
      state.verificationStatus = action.payload;
    },
    clearVerificationStatus: state => {
      // eslint-disable-next-line no-param-reassign
      state.verificationStatus = undefined;
    },
    captureSearchError: (state, action: PayloadAction<Error | undefined>) => {
      // eslint-disable-next-line no-param-reassign
      state.searchError = action.payload;
    },
    captureDisputeListError: (state, action: PayloadAction<Error | undefined>) => {
      // eslint-disable-next-line no-param-reassign
      state.disputeListError = action.payload;
    },
    captureInvolvedUsersError: (state, action: PayloadAction<Error | undefined>) => {
      // eslint-disable-next-line no-param-reassign
      state.involvedUsersError = action.payload;
    },
    captureRefundError: (state, action: PayloadAction<Error | undefined>) => {
      // eslint-disable-next-line no-param-reassign
      state.refundError = action.payload;
    },
    captureRemindError: (state, action: PayloadAction<Error | undefined>) => {
      // eslint-disable-next-line no-param-reassign
      state.remindError = action.payload;
    },
    captureVerificationsError: (state, action: PayloadAction<Error | undefined>) => {
      // eslint-disable-next-line no-param-reassign
      state.verificationsError = action.payload;
    },
    captureAccountRequirementsError: (state, action: PayloadAction<Error | undefined>) => {
      // eslint-disable-next-line no-param-reassign
      state.accountRequirementsError = action.payload;
    },
    fetchIsPaymentRequestFormValid: (state, action: PayloadAction<boolean>) => {
      // eslint-disable-next-line no-param-reassign
      state.isPaymentRequestFormValid = action.payload;
    },
    fetchIsRecipientEmailValid: (state, action: PayloadAction<boolean>) => {
      // eslint-disable-next-line no-param-reassign
      state.isRecipientEmailValid = action.payload;
    },
    fetchIsRecipientPhoneValid: (state, action: PayloadAction<boolean>) => {
      // eslint-disable-next-line no-param-reassign
      state.isRecipientPhoneValid = action.payload;
    },
    fetchIsAmountValid: (state, action: PayloadAction<boolean>) => {
      // eslint-disable-next-line no-param-reassign
      state.isAmountValid = action.payload;
    },
    fetchIsRefNumValid: (state, action: PayloadAction<boolean>) => {
      // eslint-disable-next-line no-param-reassign
      state.isRefNumValid = action.payload;
    },
    fetchIsLoadingPaymentRequests: (state, action: PayloadAction<boolean>) => {
      // eslint-disable-next-line no-param-reassign
      state.isLoadingPaymentRequests = action.payload;
    },
    fetchIsLoadingDisputes: (state, action: PayloadAction<boolean>) => {
      // eslint-disable-next-line no-param-reassign
      state.isLoadingDisputes = action.payload;
    },
    fetchRefundIdempotencyKey: (state, action: PayloadAction<string | undefined>) => {
      // eslint-disable-next-line no-param-reassign
      state.refundIdempotencyKey = action.payload;
    },
    fetchPaymentListSuccess: (state, action: PayloadAction<PaymentConnection>) => {
      // eslint-disable-next-line no-param-reassign
      state.paymentConnection = action.payload;
      // eslint-disable-next-line no-param-reassign
      let items = state.payments;

      // Initialise the array if this is the first run, or a refresh. We know its a refresh
      // when the connection contains the first page of data.
      if (!items || !state.paymentConnection.pageInfo.hasPreviousPage) {
        items = [];
      }
      action.payload.nodes.map(node => {
        if (!items?.some(i => i.id === node?.id)) {
          items?.push(node as Payment);
        }
        return node;
      });
      // eslint-disable-next-line no-param-reassign
      state.payments = items;
    },
    fetchIsLoadingPayments: (state, action: PayloadAction<boolean>) => {
      // eslint-disable-next-line no-param-reassign
      state.isLoadingPayments = action.payload;
    },
    capturePaymentListError: (state, action: PayloadAction<Error | undefined>) => {
      // eslint-disable-next-line no-param-reassign
      state.paymentListError = action.payload;
    },
    capturePaymentSearch: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line no-param-reassign
      state.paymentQueryString = action.payload;
    },
    fetchRefundErrorMessage: (state, action: PayloadAction<string | null | undefined>) => {
      // eslint-disable-next-line no-param-reassign
      state.refundErrorMessage = action.payload;
    },
    fetchIsPaymentRefunded: (state, action: PayloadAction<boolean>) => {
      // eslint-disable-next-line no-param-reassign
      state.isPaymentRefunded = action.payload;
    },
    fetchPaymentRefundedAmount: (state, action: PayloadAction<number | undefined>) => {
      // eslint-disable-next-line no-param-reassign
      state.paymentRefundedAmount = action.payload;
    },
  },
});

export const selectAccountRequirements = (state: RootState): AccountRequirements | undefined => state.home.accountRequirements;
export const selectInvolvedUsers = (state: RootState): Person[] | undefined => state.home.involvedUsers;
export const selectPaymentRequestConnection = (state: RootState): PaymentRequestConnection | undefined =>
  state.home.paymentRequestConnection;
export const selectPaymentRequests = (state: RootState): PaymentRequest[] | undefined => state.home.paymentRequests;
export const selectDisputeConnection = (state: RootState): DisputeConnection | undefined => state.home.disputeConnection;
export const selectDisputes = (state: RootState): Dispute[] | undefined => state.home.disputes;
export const selectPaymentRequestSearch = (state: RootState): string | undefined => state.home.paymentRequestQueryString;
export const selectInvoiceUniqueId = (state: RootState): string | undefined => state.home.invoiceUniqueId;
export const selectPaymentRequestStatus = (state: RootState): UpsertPaymentRequestStatus | undefined =>
  state.home.paymentRequestStatus;
export const selectReminderStatus = (state: RootState): UpsertPaymentRequestStatus | undefined => state.home.reminderStatus;
export const selectRefundStatus = (state: RootState): string | undefined => state.home.refundStatusCode;
export const selectSendPaymentRequestError = (state: RootState): string | undefined => state.home.sendPaymentRequestError;
export const selectVerificationStatus = (state: RootState): MutationStatusCode | undefined => state.home.verificationStatus?.code;
export const selectSearchError = (state: RootState): Error | undefined => state.home.searchError;
export const selectDisputeListError = (state: RootState): Error | undefined => state.home.disputeListError;
export const selectInvolvedUsersError = (state: RootState): Error | undefined => state.home.involvedUsersError;
export const selectRefundError = (state: RootState): Error | undefined => state.home.refundError;
export const selectRemindError = (state: RootState): Error | undefined => state.home.remindError;
export const selectVerificationsError = (state: RootState): Error | undefined => state.home.verificationsError;
export const selectAccountRequirementsError = (state: RootState): Error | undefined => state.home.accountRequirementsError;
export const selectIsPaymentRequestFormValid = (state: RootState): boolean => state.home.isPaymentRequestFormValid;
export const selectIsRecipientEmailValid = (state: RootState): boolean => state.home.isRecipientEmailValid;
export const selectIsRecipientPhoneValid = (state: RootState): boolean => state.home.isRecipientPhoneValid;
export const selectIsAmountValid = (state: RootState): boolean => state.home.isAmountValid;
export const selectIsRefNumValid = (state: RootState): boolean => state.home.isRefNumValid;
export const selectIsLoadingPaymentRequests = (state: RootState): boolean => state.home.isLoadingPaymentRequests;
export const selectIsLoadingDisputes = (state: RootState): boolean => state.home.isLoadingDisputes;
export const selectRefundIdempotencyKey = (state: RootState): string | undefined => state.home.refundIdempotencyKey;
export const selectPaymentConnection = (state: RootState): PaymentConnection | undefined => state.home.paymentConnection;
export const selectPayments = (state: RootState): Payment[] | undefined => state.home.payments;
export const selectPaymentSearch = (state: RootState): string | undefined => state.home.paymentQueryString;
export const selectIsLoadingPayments = (state: RootState): boolean => state.home.isLoadingPayments;
export const selectRefundErrorMessage = (state: RootState): string | null | undefined => state.home.refundErrorMessage;
export const selectIsPaymentRefunded = (state: RootState): boolean => state.home.isPaymentRefunded;
export const selectPaymentRefundedAmount = (state: RootState): number | undefined => state.home.paymentRefundedAmount;

export const {
  capturePaymentRequestStatus,
  captureReminderStatus,
  clearPaymentRequestStatus,
  clearReminderStatus,
  fetchAccountRequirementsSuccess,
  fetchInvolvedUsersSuccess,
  fetchPaymentRequestListSuccess,
  fetchDisputeListSuccess,
  capturePaymentRequestSearch,
  uploadDocumentSuccess,
  uploadDocumentFailure,
  captureRefundSuccess,
  clearRefundStatus,
  clearDocument,
  captureSendPaymentRequestError,
  captureSearchError,
  captureDisputeListError,
  captureInvolvedUsersError,
  captureRefundError,
  captureRemindError,
  captureVerificationsError,
  captureAccountRequirementsError,
  clearSendPaymentRequestError,
  captureVerificationStatus,
  clearVerificationStatus,
  fetchIsPaymentRequestFormValid,
  fetchIsRecipientEmailValid,
  fetchIsRecipientPhoneValid,
  fetchIsAmountValid,
  fetchIsRefNumValid,
  fetchIsLoadingPaymentRequests,
  fetchIsLoadingDisputes,
  fetchRefundIdempotencyKey,
  fetchPaymentListSuccess,
  fetchIsLoadingPayments,
  capturePaymentListError,
  capturePaymentSearch,
  fetchRefundErrorMessage,
  fetchIsPaymentRefunded,
  fetchPaymentRefundedAmount,
} = slice.actions;

export default slice.reducer;
